import { Typography, Grid2 as Grid, Stack, Link } from "@mui/material"
import React from "react"
import { useTranslation } from "react-i18next"
import { Check, Close } from "@mui/icons-material"
import LangLink from "./LangLink"

type DataFieldProps = {
  label: string
  value: string | number | undefined | null | string[] | number[] | React.ReactNode
  size: number | Record<string, number>
  boolean?: boolean
  link?: string
  getLink?: (value: string | number | undefined | null) => string
}

type DataFieldLinkProps = {
  value: string | number | undefined | null
  link?: string
  getLink?: (value: string | number | undefined | null) => string
  display?: string
}

export function DataFieldLink({ value, link, getLink, display="flex" }: DataFieldLinkProps) {
  const finalLink = getLink ? getLink(value) : link

  if (!finalLink) {
    return <Typography variant="body1">{value || <span>&nbsp;</span>}</Typography>
  }
  return (
    <Link
      component={LangLink}
      to={finalLink}
      color={"text.primary"}
      sx={{
        cursor: "pointer",
      }}
    >
      <Typography
        component={"span"}
        sx={{
          display: display,
          alignItems: "center",
          textDecoration: "underline",
        }}
      >
        {value}
      </Typography>
    </Link>
  )
}

export function DataFieldLabel({ label }: { label: string }) {
  return (
    <Typography variant="button" color="primary" sx={{ fontWeight: "bold" }}>
      {label}
    </Typography>
  )
}

export function DataField({ label, value, size = 12, boolean = false, link, getLink }: DataFieldProps) {
  const { t } = useTranslation()

  if (Array.isArray(value) && !link) {
    value = value.join(", ")
  }

  return (
    <Grid size={size}>
      <DataFieldLabel label={label} />

      {boolean ? (
        <>
          {value ? (
            <Stack flexDirection={"row"} alignItems={"center"}>
              <Check sx={{ color: "success.dark" }} />
              <Typography variant="body1">{t("general.si")}</Typography>
            </Stack>
          ) : (
            <Stack flexDirection={"row"} alignItems={"center"}>
              <Close sx={{ color: "error.dark" }} />
              <Typography variant="body1">{t("general.no")}</Typography>
            </Stack>
          )}
        </>
      ) : link || getLink ? (
        <DataFieldLink value={value as string | number | null} link={link} getLink={getLink} />
      ) : (
        <Typography variant="body1">{value || <span>&nbsp;</span>}</Typography>
      )}
    </Grid>
  )
}

export function DataRow({ children, last = false }: { children: React.ReactNode; last?: boolean }) {
  return (
    <Grid
      container
      spacing={2}
      columns={12}
      paddingY={2}
      sx={{
        borderBottom: last ? undefined : "1px solid",
        borderColor: last ? undefined : "grey.300",
      }}
    >
      {children}
    </Grid>
  )
}

type InlineFieldProps = {
  label: string
  value: string | number | undefined | null | string[] | number[] | React.ReactNode
  boolean?: boolean
  link?: string
}

export function InlineField({ label, value, boolean = false, link }: InlineFieldProps) {
  return (
    <Stack direction="row">
      <Typography variant="body1" color="grey.600">
        {label}:
      </Typography>
      &nbsp;
      {boolean ? (
        value ? (
          <Check sx={{ color: "success.dark" }} />
        ) : (
          <Close sx={{ color: "error.dark" }} />
        )
      ) : link ? (
        <Link
          component={LangLink}
          to={link}
          color={"text.primary"}
          sx={{
            cursor: "pointer",
          }}
        >
          {value}
        </Link>
      ) : (
        <Typography variant="body1">{value || <span>&nbsp;</span>}</Typography>
      )}
    </Stack>
  )
}
